import { Suspense, lazy } from 'react';
import { createBrowserRouter } from 'react-router';
import { NotFoundPage } from '@/components/feedback/ForbiddenPage';
import { AppShell } from '@/components/layout/AppShell';
import { FullPageSpinner } from '@/components/ui/Spinner';
import { AuthLayout } from '@/features/auth/components/AuthLayout';
import { GuestOnly, RequireAuth, RequirePermission } from '@/features/auth/guards';
import { ChangePasswordPage } from '@/features/auth/pages/ChangePasswordPage';
import { ForgotPasswordPage } from '@/features/auth/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/features/auth/pages/ResetPasswordPage';
import { SignInPage } from '@/features/auth/pages/SignInPage';

// Pages of the app shell load on demand.
const HomePage = lazy(() => import('@/features/home/HomePage'));
const CompanyProfilePage = lazy(() => import('@/features/company/CompanyProfilePage'));
const ConfigurationPage = lazy(() => import('@/features/configuration/ConfigurationPage'));
const UsersPage = lazy(() => import('@/features/users/UsersPage'));
const ProfilePage = lazy(() => import('@/features/profile/ProfilePage'));
const MobileMenuPage = lazy(() => import('@/features/menu/MobileMenuPage'));
const ProductsPage = lazy(() => import('@/features/products/ProductsPage'));
const CustomersPage = lazy(() => import('@/features/customers/CustomersPage'));
const CustomerPage = lazy(() => import('@/features/customers/CustomerPage'));
const SuppliersPage = lazy(() => import('@/features/suppliers/SuppliersPage'));
const SupplierPage = lazy(() => import('@/features/suppliers/SupplierPage'));
const SupplierPaymentsPage = lazy(() => import('@/features/suppliers/PaymentsPage'));
const SupplierPaymentPage = lazy(() => import('@/features/suppliers/PaymentPage'));
const OrdersPage = lazy(() => import('@/features/orders/OrdersPage'));
const OrderPage = lazy(() => import('@/features/orders/OrderPage'));
const OrderEditorPage = lazy(() => import('@/features/orders/OrderEditorPage'));
const DeliveryNotePage = lazy(() => import('@/features/orders/DeliveryNotePage'));
const PaymentsPage = lazy(() => import('@/features/payments/PaymentsPage'));
const CodSettlementPage = lazy(() => import('@/features/payments/CodSettlementPage'));
const DocumentPage = lazy(() => import('@/features/documents/DocumentPage'));
const DocumentsPage = lazy(() => import('@/features/documents/DocumentsPage'));
const PromotionsPage = lazy(() => import('@/features/promotions/PromotionsPage'));
const OnlineStorePage = lazy(() => import('@/features/online-store/OnlineStorePage'));
const SeoPage = lazy(() => import('@/features/online-store/SeoPage'));
const RegistersPage = lazy(() => import('@/features/registers/RegistersPage'));
const ShiftsPage = lazy(() => import('@/features/registers/ShiftsPage'));
const ShiftPage = lazy(() => import('@/features/registers/ShiftPage'));
const ZReportPage = lazy(() => import('@/features/registers/ZReportPage'));
const PosPage = lazy(() => import('@/features/pos/PosPage'));
const ProductPage = lazy(() => import('@/features/products/ProductPage'));
const CategoriesPage = lazy(() => import('@/features/catalog/CategoriesPage'));
const BrandsPage = lazy(() => import('@/features/catalog/BrandsPage'));
const ProductFormPage = lazy(() => import('@/features/products/ProductFormPage'));
const LocationsPage = lazy(() => import('@/features/locations/LocationsPage'));
const DataToolsPage = lazy(() => import('@/features/data-tools/DataToolsPage'));
const OperationsPage = lazy(() => import('@/features/operations/OperationsPage'));
const OperationPage = lazy(() => import('@/features/operations/OperationPage'));
const StockLevelsPage = lazy(() => import('@/features/stock/StockLevelsPage'));
const StockLimitsPage = lazy(() => import('@/features/stock/StockLimitsPage'));
const TransfersPage = lazy(() => import('@/features/transfers/TransfersPage'));
const TransferPage = lazy(() => import('@/features/transfers/TransferPage'));
const CountsPage = lazy(() => import('@/features/counts/CountsPage'));
const CountPage = lazy(() => import('@/features/counts/CountPage'));
const ActivityPage = lazy(() => import('@/features/activity/ActivityPage'));
const AlertsPage = lazy(() => import('@/features/alerts/AlertsPage'));
const ReportsPage = lazy(() => import('@/features/reports/ReportsPage'));

const HelpPage = lazy(() => import('@/features/help/HelpPage'));

export const router = createBrowserRouter([
  {
    element: <AuthLayout />,
    children: [
      {
        element: <GuestOnly />,
        children: [
          { path: 'sign-in', element: <SignInPage /> },
          { path: 'forgot-password', element: <ForgotPasswordPage /> },
        ],
      },
      { path: 'reset-password', element: <ResetPasswordPage /> },
      {
        element: <RequireAuth />,
        children: [{ path: 'change-password', element: <ChangePasswordPage /> }],
      },
    ],
  },
  {
    element: <RequireAuth />,
    children: [
      // POS-01: the till takes the whole screen, outside the app shell.
      {
        path: 'pos',
        element: (
          <RequirePermission anyOf={['pos:sell']}>
            <Suspense fallback={<FullPageSpinner />}>
              <PosPage />
            </Suspense>
          </RequirePermission>
        ),
      },
      {
        element: <AppShell />,
        children: [
          { index: true, element: <HomePage /> },
          {
            path: 'company',
            element: (
              <RequirePermission anyOf={['company:view']}>
                <CompanyProfilePage />
              </RequirePermission>
            ),
          },
          {
            path: 'configuration',
            element: (
              <RequirePermission anyOf={['configuration:view']}>
                <ConfigurationPage />
              </RequirePermission>
            ),
          },
          {
            path: 'users',
            element: (
              <RequirePermission anyOf={['users:view']}>
                <UsersPage />
              </RequirePermission>
            ),
          },
          {
            path: 'orders',
            children: [
              {
                index: true,
                element: (
                  <RequirePermission anyOf={['orders:view']}>
                    <OrdersPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'new',
                element: (
                  <RequirePermission anyOf={['orders:create']}>
                    <OrderEditorPage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id',
                element: (
                  <RequirePermission anyOf={['orders:view']}>
                    <OrderPage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RequirePermission anyOf={['orders:create']}>
                    <OrderEditorPage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id/delivery-note',
                element: (
                  <RequirePermission anyOf={['orders:view']}>
                    <DeliveryNotePage />
                  </RequirePermission>
                ),
              },
            ],
          },
          {
            path: 'payments',
            children: [
              {
                index: true,
                element: (
                  <RequirePermission anyOf={['payments:view']}>
                    <PaymentsPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'cod',
                element: (
                  <RequirePermission anyOf={['payments:view']}>
                    <CodSettlementPage />
                  </RequirePermission>
                ),
              },
            ],
          },
          {
            path: 'registers',
            element: (
              <RequirePermission anyOf={['registers:view', 'registers:manage']}>
                <RegistersPage />
              </RequirePermission>
            ),
          },
          {
            path: 'shifts',
            children: [
              {
                index: true,
                element: (
                  <RequirePermission anyOf={['shifts:open', 'shifts:viewAll']}>
                    <ShiftsPage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id',
                element: (
                  <RequirePermission anyOf={['shifts:open', 'shifts:close', 'shifts:viewAll']}>
                    <ShiftPage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id/z',
                element: (
                  <RequirePermission anyOf={['shifts:open', 'shifts:close', 'shifts:viewAll']}>
                    <ZReportPage />
                  </RequirePermission>
                ),
              },
            ],
          },
          {
            path: 'promotions',
            element: (
              <RequirePermission anyOf={['promotions:view']}>
                <PromotionsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'documents',
            children: [
              {
                index: true,
                element: (
                  <RequirePermission anyOf={['payments:view', 'orders:view']}>
                    <DocumentsPage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id',
                element: (
                  <RequirePermission anyOf={['payments:view', 'orders:view']}>
                    <DocumentPage />
                  </RequirePermission>
                ),
              },
            ],
          },
          {
            path: 'customers',
            children: [
              {
                index: true,
                element: (
                  <RequirePermission anyOf={['customers:view']}>
                    <CustomersPage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id',
                element: (
                  <RequirePermission anyOf={['customers:view']}>
                    <CustomerPage />
                  </RequirePermission>
                ),
              },
            ],
          },
          // Suppliers and what is paid to them (docs/SUPPLIERS_PLAN.md).
          {
            path: 'suppliers',
            children: [
              {
                index: true,
                element: (
                  <RequirePermission anyOf={['suppliers:view']}>
                    <SuppliersPage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id',
                element: (
                  <RequirePermission anyOf={['suppliers:view']}>
                    <SupplierPage />
                  </RequirePermission>
                ),
              },
            ],
          },
          {
            path: 'supplier-payments',
            children: [
              {
                index: true,
                element: (
                  <RequirePermission anyOf={['supplierPayments:view']}>
                    <SupplierPaymentsPage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id',
                element: (
                  <RequirePermission anyOf={['supplierPayments:view']}>
                    <SupplierPaymentPage />
                  </RequirePermission>
                ),
              },
            ],
          },
          {
            path: 'products',
            children: [
              {
                index: true,
                element: (
                  <RequirePermission anyOf={['products:view']}>
                    <ProductsPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'new',
                element: (
                  <RequirePermission anyOf={['products:create']}>
                    <ProductFormPage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id',
                element: (
                  <RequirePermission anyOf={['products:view']}>
                    <ProductPage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RequirePermission anyOf={['products:edit']}>
                    <ProductFormPage />
                  </RequirePermission>
                ),
              },
            ],
          },
          // R3: the catalog's lists, each on its own page.
          {
            path: 'categories',
            element: (
              <RequirePermission anyOf={['products:view']}>
                <CategoriesPage />
              </RequirePermission>
            ),
          },
          {
            path: 'online-store',
            element: (
              <RequirePermission anyOf={['products:edit', 'configuration:edit']}>
                <OnlineStorePage />
              </RequirePermission>
            ),
          },
          {
            path: 'online-store/seo',
            element: (
              <RequirePermission anyOf={['configuration:view']}>
                <SeoPage />
              </RequirePermission>
            ),
          },
          {
            path: 'brands',
            element: (
              <RequirePermission anyOf={['products:view']}>
                <BrandsPage />
              </RequirePermission>
            ),
          },
          ...['locations', 'locations/:id'].map((path) => ({
            path,
            element: (
              <RequirePermission anyOf={['locations:view']}>
                <LocationsPage />
              </RequirePermission>
            ),
          })),
          {
            path: 'operations',
            children: [
              {
                index: true,
                element: (
                  <RequirePermission anyOf={['stock:view']}>
                    <OperationsPage />
                  </RequirePermission>
                ),
              },
              ...['new/:type', ':id'].map((path) => ({
                path,
                element: (
                  <RequirePermission anyOf={['stock:view']}>
                    <OperationPage />
                  </RequirePermission>
                ),
              })),
            ],
          },
          {
            path: 'transfers',
            children: [
              {
                index: true,
                element: (
                  <RequirePermission anyOf={['transfers:view']}>
                    <TransfersPage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id',
                element: (
                  <RequirePermission anyOf={['transfers:view']}>
                    <TransferPage />
                  </RequirePermission>
                ),
              },
            ],
          },
          {
            path: 'counts',
            children: [
              {
                index: true,
                element: (
                  <RequirePermission anyOf={['counts:view']}>
                    <CountsPage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id',
                element: (
                  <RequirePermission anyOf={['counts:view']}>
                    <CountPage />
                  </RequirePermission>
                ),
              },
            ],
          },
          {
            path: 'stock',
            element: (
              <RequirePermission anyOf={['stock:view']}>
                <StockLevelsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'stock/limits',
            element: (
              <RequirePermission anyOf={['stock:view']}>
                <StockLimitsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'activity',
            element: (
              <RequirePermission anyOf={['activity:view', 'activity:viewMovements']}>
                <ActivityPage />
              </RequirePermission>
            ),
          },
          {
            path: 'alerts',
            element: (
              <RequirePermission anyOf={['alerts:view']}>
                <AlertsPage />
              </RequirePermission>
            ),
          },
          ...['reports', 'reports/:report'].map((path) => ({
            path,
            element: (
              <RequirePermission anyOf={['analytics:view']}>
                <ReportsPage />
              </RequirePermission>
            ),
          })),
          {
            path: 'data-tools',
            element: (
              <RequirePermission
                anyOf={['dataTools:import', 'dataTools:export', 'dataTools:backup']}
              >
                <DataToolsPage />
              </RequirePermission>
            ),
          },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'menu', element: <MobileMenuPage /> },
          { path: 'help', element: <HelpPage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
